import {useState, useEffect, useRef, Fragment, useMemo} from "react";
import CommonAPI from '~/api/commonApi';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faCog} from "@fortawesome/free-solid-svg-icons";

function Table({ tableConfig }) {
    const {columns = [], data = [], tableKey = "Empty", hasShowHideColumn = true, extraButtons = [] } = tableConfig;
    const [expandedCells, setExpandedCells] = useState({});
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [tempColumns, setTempColumns] = useState([]);
    const [showAction, setShowAction] = useState(false);

    const orderedColumns = useMemo(() => {
        const cols = [...columns];
        const actionIndex = cols.findIndex(c => c.key === "Action");
        if (actionIndex === -1) return cols;

        const [actionCol] = cols.splice(actionIndex, 1);

        if (showAction) {
            cols.splice(1, 0, actionCol);
        } else {
            cols.push(actionCol);
        }

        return cols;
    }, [columns, showAction]);


    useEffect(() => {
        CommonAPI.getColumnSetting(tableKey)
            .then(res => {
                let cols = [];

                if (res.data?.VisibleColumns) {
                    cols = JSON.parse(res.data.VisibleColumns || "[]");
                } else {
                    cols = columns.map(c => c.key);
                }

                setVisibleColumns(cols);
                setTempColumns(cols);
                setShowAction(res.data.IsShowAction);
            })
            .catch(err => {});
    }, [tableKey, columns]);

    const toggleTempColumn = (key) => {
        setTempColumns(prev =>
            prev.includes(key)
                ? prev.filter(c => c !== key)
                : [...prev, key]
        );
    };

    const visibleColumnsRef = useRef([]);
    useEffect(() => {
        visibleColumnsRef.current = visibleColumns;
    }, [visibleColumns]);


    const handleSaveChangesColumnConfig = () => {
        const payload = {
            TableKey: tableKey,
            VisibleColumns: JSON.stringify(tempColumns),
            IsShowAction: false,
            Action: "ColumnSetting"
        }
        CommonAPI.insUpdColumnSetting(payload)
            .then(res => {
                setVisibleColumns([...tempColumns]);
                visibleColumnsRef.current = [...tempColumns];
            })
            .catch(err => {})
            .finally(() => {
                const dropdownElement = document.getElementById("dropdownColumnConfig");
                const dropdownInstance = window.bootstrap.Dropdown.getInstance(dropdownElement);
                dropdownInstance.hide();
            });
    };

    const toggleCell = (rowIndex, colKey) => {
        const cellId = `${rowIndex}-${colKey}`;
        setExpandedCells(prev => ({
            ...prev,
            [cellId]: !prev[cellId]
        }));
    };

    useEffect(() => {
        const dropdownElement = document.getElementById("dropdownColumnConfig");
        if (!dropdownElement) return;

        dropdownElement.addEventListener("hidden.bs.dropdown", () => {
            setTempColumns(visibleColumnsRef.current);
        });

        return () => {
            dropdownElement.removeEventListener("hidden.bs.dropdown", () => {});
        };
    }, []);

    const toggleShowAction = (checked) => {
        setShowAction(checked);
        CommonAPI.insUpdColumnSetting(
            {
                TableKey: tableKey,
                IsShowAction: checked,
                VisibleColumns: '[]',
                Action: "ShowAction",
            }
        ).catch(() => {});
    };

    return (
        <div>
            <div className="form-check mb-1 pt-2 pb-2">
                <input
                    type="checkbox"
                    className="form-check-input cursor-pointer"
                    id="chk-show-action"
                    checked={showAction}
                    onChange={(e) => toggleShowAction(e.target.checked)}
                />
                <label
                    className="form-check-label fs-9 fw-bold"
                    htmlFor="chk-show-action"
                >
                    Show action on left side
                </label>
            </div>
            {
                hasShowHideColumn &&
                <div className="dropdown mt-3 mb-5 d-flex align-items-center">
                    <a href='#!' data-bs-toggle="dropdown" role="button"
                       className="text-primary d-inline-block fw-bold nav-link dropdown-toggle dropdown-caret-none fs-9 text-decoration-underline"
                       aria-expanded="false"
                       id="dropdownColumnConfig"
                       data-bs-auto-close = "outside"
                    > <FontAwesomeIcon icon={faCog} className="me-1" />
                        Show/Hide Column Config
                    </a>

                    {
                        extraButtons.length > 0 &&
                        <Fragment>
                            {extraButtons.map((btn, idx) => (
                                <div key={idx}>{btn}</div>
                            ))}
                        </Fragment>
                    }
                    <div
                        className="dropdown-menu navbar-dropdown-caret py-0 shadow border"
                        aria-labelledby="dropdownColumnConfig"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="card position-relative border-0"
                            style={{maxHeight: '500px', overflowX: 'hidden', overflowY: 'auto'}}>
                            <div className="card-header mb-2 py-3">
                                <div className="text-center">
                                    <button onClick={handleSaveChangesColumnConfig} className="btn btn-bt-primary">Save Changes</button>
                                </div>
                            </div>
                            <div className="card-body p-0">
                                <div className="px-3 py-2">
                                    <div className="g-0 row" style={{maxWidth: '380px'}}>
                                        {orderedColumns.map((col, i) => (
                                            <div key={i} className="col-sm-6">
                                                <div className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input cursor-pointer"
                                                        id={`chk-${col.key}`}
                                                        checked={tempColumns.includes(col.key)}
                                                        onChange={() => toggleTempColumn(col.key)}
                                                    />
                                                    <label
                                                        className="form-check-label fs-9 fw-semibold"
                                                        htmlFor={`chk-${col.key}`}
                                                    >
                                                        {col.name.toLowerCase()
                                                            .replace(/\b\w/g, (char) => char.toUpperCase())}
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
            <div className="table-responsive scrollbar ms-n1 ps-1" style={{minHeight: '250px'}}>
                <table className="table table-sm fs-9 mb-0">
                    <thead>
                    <tr>
                        {orderedColumns.map((col, index) =>
                            visibleColumns.includes(col.key) ? (
                                <th
                                    key={index}
                                    className={`align-middle ${col.headerClassName ? col.headerClassName : ""} ${index === 0 ? "ps-0" : ""}`}
                                    style={{ minWidth: col.width || 'auto' }}
                                >
                                    {col.type === "checkbox" ? (
                                        <div className="form-check fs-8 mb-0">
                                            <input type="checkbox" className="form-check-input" />
                                        </div>
                                    ) : (
                                        col.name || ""
                                    )}
                                </th>
                            ) : null
                        )}
                    </tr>
                    </thead>
                    <tbody>
                    {data.length === 0 ? (
                        <tr>
                            <td colSpan={visibleColumns.length} className="text-center py-4 fs-8-1 text-muted fw-semibold">
                                No record found
                            </td>
                        </tr>
                    ) : (
                    data.map((row, rIndex) => (
                        <tr key={rIndex}>
                            {orderedColumns.map((col, cIndex) => {
                                if (!visibleColumns.includes(col.key)) return null;

                                const cellId = `${rIndex}-${col.key}`;
                                const content = col.template ? col.template(row) : row[col.key];
                                const isExpanded = expandedCells[cellId];
                                const shouldTruncate = col.isMoreLess && typeof content === "string" && content.length > 100;
                                const displayContent = shouldTruncate && !isExpanded ? content.slice(0, 100) + "..." : content;

                                return (
                                    <td
                                        key={cIndex}
                                        className={`align-middle pt-2 pb-2 ${col.className ? col.className : ""}`}
                                    >
                                        {displayContent}
                                        {shouldTruncate && (
                                            <a
                                                href="#"
                                                className="ms-2 text-primary"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    toggleCell(rIndex, col.key);
                                                }}
                                            >
                                                {isExpanded ? "less" : "more"}
                                            </a>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    )))
                    }
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default Table;
